import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_PLATFORM_BRANDING,
  withOryaTheme,
  type PlatformBrandingPublicDto,
  applyPlatformBrandingToDocument,
  getPublicPlatformBrandingUrl,
} from '@/core/platform-branding/platform-branding';
import { BrandingUiContext } from './branding-ui-context';

export function PlatformBrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PlatformBrandingPublicDto>(DEFAULT_PLATFORM_BRANDING);

  const apply = useCallback((dto: PlatformBrandingPublicDto) => {
    applyPlatformBrandingToDocument(dto);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(getPublicPlatformBrandingUrl());
      if (!res.ok) throw new Error('branding fetch failed');
      const data = withOryaTheme((await res.json()) as PlatformBrandingPublicDto);
      setBranding(data);
      apply(data);
    } catch {
      setBranding(DEFAULT_PLATFORM_BRANDING);
      apply(DEFAULT_PLATFORM_BRANDING);
    }
  }, [apply]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getPublicPlatformBrandingUrl());
        if (!res.ok) throw new Error('branding fetch failed');
        const data = withOryaTheme((await res.json()) as PlatformBrandingPublicDto);
        if (cancelled) return;
        setBranding(data);
        apply(data);
      } catch {
        if (cancelled) return;
        setBranding(DEFAULT_PLATFORM_BRANDING);
        apply(DEFAULT_PLATFORM_BRANDING);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const value = useMemo(
    () => ({
      branding,
      refresh,
    }),
    [branding, refresh]
  );

  return <BrandingUiContext.Provider value={value}>{children}</BrandingUiContext.Provider>;
}
